import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SFTextField from "../textField";
import SFTypography from "../typography";
import { SFStack } from "../grid";
import {
  getMonthDayYearDate,
  getUtcDateTime,
} from "../../services/dateService";
import moment from "moment";

const SFDatePicker = ({
  label,
  value,
  onChange,
  disablePortal,
  dateFormatter,
  preventUserInput,
  format,
  disableClear,
  ...restProps
}) => {
  const { t } = useTranslation();

  const onKeyDown = (e) => {
    if (preventUserInput) {
      e.preventDefault();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DatePicker
        label={label ?? t("components.datePicker.dateLabel")}
        value={value}
        onChange={(date) => {
          if (!date) return onChange(null);
          onChange(
            dateFormatter
              ? dateFormatter(date)
              : getUtcDateTime(getMonthDayYearDate(date))
          );
        }}
        PopperProps={{
          disablePortal,
        }}
        componentsProps={{
          actionBar: {
            actions: disableClear ? [] : ["clear"],
          },
        }}
        renderInput={(params) => {
          const inputProps = { ...params.inputProps };
          if (format && inputProps.value) {
            inputProps.value = moment(
              params.inputProps.value,
              "MM/DD/YYYY"
            ).format(format);
          }
          return (
            <SFTextField
              {...params}
              inputProps={inputProps}
              variant="filled"
              onKeyDown={onKeyDown}
            />
          );
        }}
        {...restProps}
      />
    </LocalizationProvider>
  );
};

SFDatePicker.defaultProps = {
  disablePortal: false,
  preventUserInput: false,
  disableClear: false,
};

SFDatePicker.propTypes = {
  // Label for the input field
  label: PropTypes.string,
  // Controlled value for the date. A moment.js date object
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  // Whether the picker popup is rendered in the current element
  // or in a portal
  disablePortal: PropTypes.bool,
  // If the user can type a date in the input field
  preventUserInput: PropTypes.bool,
  // Determines the format of the date when rendered in the input field. Must be a valid moment.js format string.
  format: PropTypes.string,
  // Removes the clear button from the picker action bar
  disableClear: PropTypes.bool,
};

export const SFDateRangePicker = ({
  label,
  values,
  onChange,
  onError,
  disablePortal,
  dateFormatter,
  preventUserInput,
  format,
  ...restProps
}) => {
  const { t } = useTranslation();
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <SFStack direction="row" spacing={2} alignItems="center">
        <SFDatePicker
          label={t("components.datePicker.fromLabel")}
          value={values.dateFrom}
          onChange={(date) => {
            onChange({ dateFrom: date, dateTo: values.dateTo });
          }}
          disablePortal={disablePortal}
          maxDate={values.dateTo}
          dateFormatter={dateFormatter}
          preventUserInput={preventUserInput}
          format={format}
          {...restProps}
        />
        <SFTypography variant="formElementText">-</SFTypography>
        <SFDatePicker
          label={t("components.datePicker.toLabel")}
          value={values.dateTo}
          onChange={(date) =>
            onChange({ dateFrom: values.dateFrom, dateTo: date })
          }
          disablePortal={disablePortal}
          minDate={values.dateFrom}
          dateFormatter={dateFormatter}
          preventUserInput={preventUserInput}
          format={format}
          {...restProps}
        />
      </SFStack>
    </LocalizationProvider>
  );
};

SFDateRangePicker.defaultProps = {
  disablePortal: false,
  preventUserInput: false,
};

SFDateRangePicker.propTypes = {
  values: PropTypes.shape({
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  disablePortal: PropTypes.bool,
  preventUserInput: PropTypes.bool,
  format: PropTypes.string,
};

export default SFDatePicker;
